import { Stack, TextField, Typography } from '@mui/material';
import {
  isValidLandingPageSlug,
  isValidLandingPageUrl,
  LANDING_PAGE_SLUG_MAX_LENGTH,
  LANDING_PAGE_SLUG_RESERVED_MESSAGE,
  RESERVED_LANDING_PAGE_SLUGS,
} from '@provision/landing-page-utils';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { FormSwitch } from 'src/components/Forms/FormSwitch/form-switch';
import { ColorPickerField } from 'src/pages/Settings/memberships/components/color-picker-field';
import { HeaderSettingsFields } from 'src/pages/Settings/memberships/components/landing-page-tab/header-settings-fields';
import { SegmentedToggleField } from 'src/pages/Settings/memberships/components/landing-page-tab/segmented-toggle-field';
import {
  CTA_MODE_OPTIONS,
  type LandingPageFormValues,
} from 'src/pages/Settings/memberships/components/landing-page-tab/types';
import { panelFieldLabelSx } from 'src/pages/Settings/memberships/styles';
import { COLOR_FIELD_RULES } from 'src/pages/Settings/memberships/utils/color-field-rules';

const CTA_NOTE =
  'Consultation links to your scheduling URL. Sign up sends clients into the activation flow; magic links log them in automatically.';

interface LandingPageSettingsTabProps {
  readOnly: boolean;
}

export const LandingPageSettingsTab = ({ readOnly }: LandingPageSettingsTabProps) => {
  const { control } = useFormContext<LandingPageFormValues>();
  const ctaMode = useWatch({ control, name: 'settings.ctaMode' });

  return (
    <Stack p={2} gap={3}>
      <Controller
        name="name"
        control={control}
        rules={{ required: 'Enter a page name.' }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label="Page name"
            size="small"
            disabled={readOnly}
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message ?? 'Shown to your team, not to visitors.'}
          />
        )}
      />

      <Controller
        name="slug"
        control={control}
        rules={{
          required: 'Enter a URL slug.',
          validate: (value) => {
            if (RESERVED_LANDING_PAGE_SLUGS.includes(value)) {
              return LANDING_PAGE_SLUG_RESERVED_MESSAGE;
            }

            return (
              isValidLandingPageSlug(value) ||
              'Use lowercase letters, numbers and single hyphens, 2 to 48 characters.'
            );
          },
        }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label="URL slug"
            size="small"
            disabled={readOnly}
            inputProps={{ maxLength: LANDING_PAGE_SLUG_MAX_LENGTH }}
            error={Boolean(fieldState.error)}
            helperText={
              fieldState.error?.message ?? 'The last part of the public page address.'
            }
          />
        )}
      />

      <Stack gap={1.5}>
        <SegmentedToggleField
          name="settings.ctaMode"
          label="Call to action"
          options={CTA_MODE_OPTIONS}
          readOnly={readOnly}
        />
        {ctaMode === 'consultation' ? (
          <Controller
            name="settings.consultationUrl"
            control={control}
            rules={{
              validate: (value) =>
                isValidLandingPageUrl(value.trim()) || 'Enter the scheduling URL (https://…).',
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Scheduling URL"
                size="small"
                disabled={readOnly}
                placeholder="https://calendly.com/your-team"
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
        ) : null}
        <Typography variant="body2" color="text.secondary">
          {CTA_NOTE}
        </Typography>
      </Stack>

      <FormSwitch
        type="switch"
        name="settings.showPricing"
        label="Show pricing"
        labelPlacement="end"
        disabled={readOnly}
        sx={{ m: 0, alignSelf: 'flex-start' }}
      />

      <HeaderSettingsFields readOnly={readOnly} />

      <Stack gap={1}>
        <Typography variant="caption" sx={panelFieldLabelSx}>
          Brand color
        </Typography>
        <Stack sx={{ '& > *': { borderBottom: 1, borderColor: 'divider' } }}>
          <Controller
            name="settings.primaryColor"
            control={control}
            rules={COLOR_FIELD_RULES}
            render={({ field, fieldState }) => (
              <ColorPickerField
                field={field}
                label="Primary"
                readOnly={readOnly}
                hasError={Boolean(fieldState.error)}
              />
            )}
          />
          <Controller
            name="settings.secondaryColor"
            control={control}
            rules={COLOR_FIELD_RULES}
            render={({ field, fieldState }) => (
              <ColorPickerField
                field={field}
                label="Secondary"
                readOnly={readOnly}
                hasError={Boolean(fieldState.error)}
              />
            )}
          />
          <Controller
            name="settings.accentColor"
            control={control}
            rules={COLOR_FIELD_RULES}
            render={({ field, fieldState }) => (
              <ColorPickerField
                field={field}
                label="Accent"
                readOnly={readOnly}
                hasError={Boolean(fieldState.error)}
              />
            )}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
